import { Subject } from 'rxjs';
import { AlcmeonContextService } from 'src/app/core/alcmeon-context/alcmeon-context.service';
import { AttachmentType, Editor2Answer } from 'src/app/shared/models/misc.model';
import { BasicService } from 'src/app/shared/services/basic.service';
import { UploadService } from 'src/app/shared/services/upload.service';

import { EditorConfig } from '../../editor-config';
import { Attachments } from './attachments';
import { SingleMessage } from './single-message';

export class SingleMessages {
  messages: SingleMessage[] = [];
  messagesUpdated$ = new Subject<void>();
  updateValidity$ = new Subject<boolean>();

  areAllMessagesValid = true;

  siteId: number;
  editorConfig: EditorConfig;

  constructor(
    private basicService: BasicService,
    private uploadService: UploadService,
    private alcmeonContext: AlcmeonContextService
  ) {}

  setSiteId(siteId: number) {
    this.siteId = siteId;
  }

  setEditorConfig(editorConfig: EditorConfig) {
    this.editorConfig = editorConfig;
  }

  initWithEditor2Answers(answers: Editor2Answer[]) {
    this.messages = answers.map(answer => {
      const attachments = new Attachments(this, this.basicService, this.uploadService, this.alcmeonContext);
      answer.attachments.forEach(attachment => {
        switch (attachment.type) {
          case AttachmentType.Picture:
            attachments.addPictureAttachment(attachment.value.pictureId, attachment.mimeType);
            break;
          case AttachmentType.Video:
            attachments.addVideoAttachment(attachment.value.videoId);
            break;
        }
      });

      const message = new SingleMessage(this, attachments, this.editorConfig);
      message.text = answer.text;

      return message;
    });
    this.updateIsLastAndIsOnly();
    this.updateValidity();
    this.notifyMessagesUpdated();
  }

  // adds a new single message after the message at the given index, or at the beginning if index is not given
  // returns the newly created AddedMessage
  addMessage(index = -1) {
    const attachments = new Attachments(this, this.basicService, this.uploadService, this.alcmeonContext);
    const message = new SingleMessage(this, attachments, this.editorConfig);
    this.messages.splice(index + 1, 0, message);
    this.updateIsLastAndIsOnly();
    this.updateValidity();
    this.notifyMessagesUpdated();
    return message;
  }

  addMessageAfter(message: SingleMessage) {
    const addedMessage = this.addMessage(this.messages.indexOf(message));
    this.updateIsFocused(addedMessage); // update focus status ASAP to avoid button bar flashing in/out
    addedMessage.doFocusAfterTimeout();
  }

  removeMessage(message: SingleMessage) {
    const index = this.messages.indexOf(message);
    this.messages.splice(index, 1);
    this.updateIsLastAndIsOnly();
    const focusIndex = index < this.messages.length ? index : index - 1;
    this.messages[focusIndex].doFocus();
    this.updateIsFocused(this.messages[focusIndex]); // update focus status ASAP to avoid button bar flashing in/out
    this.updateValidity();
    this.notifyMessagesUpdated();
  }

  removeAllMessages() {
    this.messages = [];
    this.updateValidity();
    this.notifyMessagesUpdated();
  }

  notifyMessagesUpdated() {
    this.messagesUpdated$.next();
  }

  lastMessage() {
    return this.messages[this.messages.length - 1];
  }

  updateIsFocused(focusedMessage) {
    this.messages.forEach(message => (message.isFocused = message === focusedMessage));
  }

  updateValidity() {
    this.areAllMessagesValid = this.messages.every(message => message.isValid());
    this.updateValidity$.next(this.areAllMessagesValid);
  }

  getEditor2Answer(): Editor2Answer[] {
    return this.messages.map(message => ({
      text: message.text,
      attachments: message.attachments.getAttachments(),
      links: message.links.map(link => ({ ...link, wrap: message.areLinkedShortened })),
      add_switch_to_dm_link: message.isDMSwitchChecked,
    }));
  }

  areAllEditorsEmpty() {
    for (const message of this.messages) {
      if (message.text) {
        return false;
      }
    }
    return true;
  }

  /**
   * Called when switching from public to private or private to public,
   * to re-create the messages so that angular rebuilds the <app-single-message-editor>s
   */
  switchReplyIn(newEditorConfig: EditorConfig) {
    this.editorConfig = newEditorConfig;
    const currentText = this.messages.map(message => message.text).reduce((acc, cur) => `${acc}\n${cur}`);
    this.removeAllMessages();
    const newMessage = this.addMessage();
    newMessage.text = currentText;
    newMessage.doFocusAfterTimeout(); // make the toolbar visible asap
    setTimeout(() => newMessage.doFocus(), 700); // regain focus ( I think ionic-popover's animation is the culprit)
  }

  forceHideAttachments(forceHideAttachments: boolean) {
    for (const message of this.messages) {
      message.forceHideAttachments(forceHideAttachments);
    }
  }

  private updateIsLastAndIsOnly() {
    for (let i = 0; i < this.messages.length; i++) {
      this.messages[i].isLast = i === this.messages.length - 1;
      this.messages[i].isOnlyMessage = this.messages.length === 1;
    }
  }
}
